<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <!-- Progress alert (regular user) -->
        <v-alert
          v-if="!isAdmin && assignmentsData && assignmentsData.progress.total > 0"
          :type="assignmentsData.progress.remaining === 0 ? 'success' : 'info'"
          variant="tonal"
          class="mb-4"
          density="compact"
        >
          <span v-if="assignmentsData.progress.remaining === 0">
            All {{ assignmentsData.progress.total }} reviews completed for {{ clientDateFormatted }}
          </span>
          <span v-else>
            {{ assignmentsData.progress.copied }} of {{ assignmentsData.progress.total }} completed
            for {{ clientDateFormatted }} &mdash; {{ assignmentsData.progress.remaining }} remaining
          </span>
        </v-alert>

        <!-- Admin overall progress alert -->
        <v-alert
          v-if="isAdmin && adminData && adminData.users.length > 0"
          :type="adminOverallProgress.remaining === 0 ? 'success' : 'info'"
          variant="tonal"
          class="mb-4"
          density="compact"
        >
          <span v-if="adminOverallProgress.remaining === 0">
            All {{ adminOverallProgress.total }} reviews completed for {{ clientDateFormatted }} across
            {{ adminData.users.length }} users
          </span>
          <span v-else>
            {{ adminOverallProgress.copied }} of {{ adminOverallProgress.total }} completed
            for {{ clientDateFormatted }} &mdash; {{ adminOverallProgress.remaining }} remaining across
            {{ adminData.users.length }} users
          </span>
        </v-alert>

        <!-- Loading spinner -->
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate></v-progress-circular>
          <div class="mt-2">Loading assignments...</div>
        </div>

        <!-- Card view -->
        <ReviewsAssignmentsCardView v-else-if="viewMode === 'cards'" />

        <!-- Table view -->
        <ReviewsAssignmentsTableView v-else-if="viewMode === 'list'" />

        <!-- Not loaded yet fallback -->
        <v-empty-state
          v-else-if="!loading && !hasLoaded"
          class="text-center py-8"
          headline="No reviews loaded yet"
          text=""
          icon="mdi-message-text-outline"
          action-text="Load Reviews"
          @click:action="fetchAssignments"
        />
      </v-col>
    </v-row>

    <!-- Upload Screenshot Dialog -->
    <ReviewCompletionDialog
      v-model="completionDialogOpen"
      :entry="selectedEntry"
      :review="selectedReview"
      @completed="onUploadCompleted"
    />

    <!-- Add/Edit/Duplicate Assignment Dialog (admin only) -->
    <ReviewScheduleEditDialog
      v-model="editDialogOpen"
      :mode="editDialogMode"
      :entry="editDialogEntry"
      @saved="fetchAssignments"
    />

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script setup>
/**
 * Orchestrator for the daily review assignments page.
 * Delegates rendering to CardView or TableView based on viewMode toggle.
 */
import { computed, watchEffect, onMounted, onBeforeUnmount } from "vue";

import { useAppBarActions } from "@/composables/useAppBarActions.js";
import { useReviewsDailyState } from "@/composables/useReviewsDailyState.js";
import ReviewsAssignmentsCardView from "@/views/reviews/ReviewsAssignmentsCardView.vue";
import ReviewsAssignmentsTableView from "@/views/reviews/ReviewsAssignmentsTableView.vue";
import ReviewCompletionDialog from "@/components/ReviewCompletionDialog.vue";
import ReviewScheduleEditDialog from "@/components/ReviewScheduleEditDialog.vue";

const { setActions, clearActions } = useAppBarActions();

const {
  isAdmin,
  loading,
  hasLoaded,
  assignmentsData,
  adminData,
  adminOverallProgress,
  snackbar,
  completionDialogOpen,
  selectedEntry,
  selectedReview,
  viewMode,
  clientDateFormatted,
  editDialogOpen,
  editDialogMode,
  editDialogEntry,
  fetchAssignments,
  onUploadCompleted,
  toggleViewMode,
} = useReviewsDailyState();

// Register app bar actions — view toggle + coverage report + refresh
watchEffect(() => {
  setActions([
    {
      icon: viewMode.value === "cards" ? "mdi-view-list" : "mdi-view-grid",
      tooltip: viewMode.value === "cards" ? "Switch to list view" : "Switch to card view",
      onClick: toggleViewMode,
    },
    // { icon: "mdi-refresh", tooltip: "Refresh", onClick: fetchAssignments, loading: loading.value },
    { icon: "mdi-plus", tooltip: "Add Assignment", onClick: () => { editDialogMode.value = "add"; editDialogEntry.value = null; editDialogOpen.value = true; }, show: isAdmin.value },
  ]);
});
onBeforeUnmount(() => clearActions());

onMounted(() => {
  fetchAssignments();
});
</script>
